import React, { useEffect, useRef, useState } from 'react';
import { useScreen } from './useScreen';

// ═══ GLOBAL CSS ═══
export function ImmersiveGlobalStyles() {
  return (
    <style>{`
      @keyframes iridescent { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      @keyframes iriGlow { 0% { box-shadow: 0 0 25px rgba(93,193,185,0.35); } 33% { box-shadow: 0 0 25px rgba(253,190,2,0.35); } 66% { box-shadow: 0 0 25px rgba(235,99,107,0.35); } 100% { box-shadow: 0 0 25px rgba(93,193,185,0.35); } }
      @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      @keyframes pulse { 0%, 100% { box-shadow: 0 0 20px rgba(93,193,185,0.3); } 50% { box-shadow: 0 0 40px rgba(93,193,185,0.5); } }
      @keyframes floatGlow1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(80px,50px) scale(1.1); } 66% { transform: translate(-30px,80px) scale(0.95); } }
      @keyframes floatGlow2 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-60px,40px) scale(1.05); } 66% { transform: translate(50px,-30px) scale(1.1); } }
      @keyframes floatGlow3 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(50px,-60px) scale(1.08); } 66% { transform: translate(-40px,50px) scale(0.95); } }
      @keyframes blurReveal { from { filter: blur(12px); opacity:0; transform:translateY(40px) scale(0.96); } to { filter: blur(0); opacity:1; transform:translateY(0) scale(1); } }
      .td-iri-text {
        background: linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b, #534AB7, #1D9E75, #5dc1b9);
        background-size: 300% 300%; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: iridescent 4s ease infinite;
      }
      .td-glow-btn {
        box-shadow: 0 0 25px rgba(93,193,185,0.3), 0 0 50px rgba(93,193,185,0.1);
        transition: box-shadow 0.3s, transform 0.3s;
      }
      .td-glow-btn:hover {
        box-shadow: 0 0 50px rgba(93,193,185,0.6), 0 0 100px rgba(93,193,185,0.25);
        transform: translateY(-3px) scale(1.03);
      }
      .td-blur-reveal { animation: blurReveal 0.8s ease-out both; }
      @keyframes pageIn { from { opacity:0; transform:scale(0.98); } to { opacity:1; transform:scale(1); } }
      body { animation: pageIn 0.5s ease-out both; }
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; }
        .td-iri-text, .td-glow-btn, .td-blur-reveal, .td-pulse-btn { animation: none !important; }
        .td-glow-btn:hover { transform: none; }
        body { animation: none !important; }
      }
    `}</style>
  );
}

// CustomCursor removed — using system cursor

// ═══ AURORA — 3 large blurred glows, filter blur(80px), opacity 0.15 ═══
export function AuroraBackground() {
  const { isMobile } = useScreen();
  if (isMobile) return null; // Skip on mobile for performance
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-15%', left: '-5%', width: 550, height: 550, borderRadius: '50%', background: 'radial-gradient(circle, rgba(93,193,185,0.15) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'floatGlow1 18s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', top: '25%', right: '-10%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(253,190,2,0.12) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'floatGlow2 23s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: '-10%', left: '15%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(235,99,107,0.12) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'floatGlow3 20s ease-in-out infinite' }} />
    </div>
  );
}

// ═══ SCROLL BLUR REVEAL ═══
export function useBlurReveal() {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return { ref, style: { filter: revealed ? 'blur(0)' : 'blur(12px)', opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.96)', transition: 'filter 0.8s ease-out, opacity 0.8s ease-out, transform 0.8s ease-out' } };
}

// ═══ ANIMATED COUNTER ═══
export function AnimatedCounter({ end, duration = 2000, inView = true }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView || !end) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => { start += step; if (start >= end) { setVal(end); clearInterval(timer); } else setVal(Math.floor(start)); }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);
  return <span>{val.toLocaleString()}</span>;
}

// ═══ PARTICLES WITH MOUSE REPULSION + CONNECTION LINES ═══
export function SubtleParticles({ height = 200, count = 20 }) {
  const ref = useRef(null);
  const anim = useRef(null);
  const mouse = useRef({ x: -999, y: -999 });
  const { isMobile } = useScreen();

  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const colors = ['#5dc1b9', '#FDBE02', '#eb636b'];
    let w, h;

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = canvas.width = rect.width;
      h = canvas.height = height;
    }
    resize();

    const num = isMobile ? Math.min(count, 15) : count;
    const particles = Array.from({ length: num }, () => {
      const x = Math.random() * (w || 800);
      const y = Math.random() * height;
      return {
        x, y, origX: x, origY: y,
        r: 1.5 + Math.random() * 2.5,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: 0.2 + Math.random() * 0.3,
      };
    });

    // Mouse tracking relative to canvas
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onLeave = () => { mouse.current = { x: -999, y: -999 }; };
    if (!isMobile) {
      canvas.parentElement.addEventListener('mousemove', onMove);
      canvas.parentElement.addEventListener('mouseleave', onLeave);
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);

      // Connection lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.strokeStyle = `rgba(93,193,185,${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        // Mouse repulsion
        if (!isMobile) {
          const dx = p.x - mouse.current.x;
          const dy = p.y - mouse.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100 && dist > 0) {
            const force = (100 - dist) / 100;
            p.x += (dx / dist) * force * 3;
            p.y += (dy / dist) * force * 3;
          }
          // Spring back to original position
          p.x += (p.origX - p.x) * 0.02;
          p.y += (p.origY - p.y) * 0.02;
        }

        // Drift
        p.origX += p.vx;
        p.origY += p.vy;
        if (!isMobile) {
          // Bound originals
          if (p.origX < 0 || p.origX > w) p.vx *= -1;
          if (p.origY < 0 || p.origY > h) p.vy *= -1;
          p.origX = Math.max(0, Math.min(w, p.origX));
          p.origY = Math.max(0, Math.min(h, p.origY));
        } else {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > w) p.vx *= -1;
          if (p.y < 0 || p.y > h) p.vy *= -1;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      anim.current = requestAnimationFrame(draw);
    }
    draw();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(anim.current);
      window.removeEventListener('resize', resize);
      if (!isMobile) {
        canvas.parentElement?.removeEventListener('mousemove', onMove);
        canvas.parentElement?.removeEventListener('mouseleave', onLeave);
      }
    };
  }, [count, height, isMobile]);

  return <canvas ref={ref} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height, pointerEvents: 'none', zIndex: 0 }} />;
}
